/**
 * Label for Min Max from API that resolves to unknown
 */
export const UNKNOWN = "Unknown";

/**
 * Default employee ranges FROM the API
 **/
export const DEFAULT_API_EMPLOYEE_RANGES = [
  { name: "Less than 10", range: { min: 1, max: 9 } },
  { name: "10 to 49", range: { min: 10, max: 49 } },
  { name: "50 to 199", range: { min: 50, max: 199 } },
  { name: "200 to 499", range: { min: 200, max: 499 } },
  { name: "500 to 999", range: { min: 500, max: 999 } },
  { name: "1,000 to 4,999", range: { min: 1000, max: 4999 } },
  { name: "5,000 to 9,999", range: { min: 5000, max: 9999 } },
  { name: "Above 10,000", range: { min: 10_000, max: -1 } },
  { name: UNKNOWN, range: { min: -1, max: -1 } },
];

/**
 * Default revenue ranges FROM the API
 **/
export const DEFAULT_API_REVENUE_RANGES = [
  { name: "Less than $1m", range: { min: 1, max: 999_999 } },
  { name: "$1m to $10m", range: { min: 1_000_000, max: 9_999_999 } },
  { name: "$10m to $50m", range: { min: 10_000_000, max: 49_999_999 } },
  { name: "$50m to $100m", range: { min: 50_000_000, max: 99_999_999 } },
  {
    name: "$100m to $200m",
    range: { min: 100_000_000, max: 199_999_999 },
  },
  {
    name: "$200m to $500m",
    range: { min: 200_000_000, max: 499_999_999 },
  },
  { name: "$500m to $1bn", range: { min: 500_000_000, max: 999_999_999 } },
  { name: "Over $1bn", range: { min: 1_000_000_000, max: -1 } },
  { name: UNKNOWN, range: { min: -1, max: -1 } },
];

// export const COMPETITOR_MARKET_PENETRATION_TREND_TOP_PRODUCTS = 5;
export const INSUFFICIENT_WHITESPACE_INFO_TEXT =
  "Set up whitespace to get more insights regarding your market";
export const INSUFFICIENT_COMPETITORS_INFO_TEXT =
  "Set up competitors to get more insights regarding them";

export const CHART_CONSTANTS = {
  // Competitive Analysis
  competitorMarketPenetration: {
    chartTitle: "Competitor Market Penetration",
    baseId: "competitor-market-penetration",
    csvFieldMappings: {
      productName: "Product Name",
      companyCount: "# of Companies",
      percentage: "Market Penetration (%)",
    },
    tooltip:
      "Ranks top 10 competitive products by installation count, showing the number of companies where each product is installed. Percentages indicate market penetration.\n\nIdentify your most significant competitors and understand their relative market position.",
    topProducts: 10,
    trendTopProducts: 5,
  },
  competitorMarketPenetrationBySegment: {
    chartTitle: "Competitor Market Penetration by Segment",
    baseId: "competitor-market-penetration-by-segment",
    tooltip:
      "Compares competitor penetration rates across different market segments. For each segment, shows the top 5 competitors by penetration.\n\nNote: Competitors may have presence in other segments but only appear where they rank in the top 5 for that specific segment.\n\nIdentify segments where competitors are strongest or weakest to guide your go-to-market strategy.",
  },
  competitorNewProductDetections: {
    chartTitle: "Competitor New Product Detections",
    baseId: "competitor-new-product-detections",
    csvFieldMappings: {
      productName: "Product Names",
      hits: "New Detections",
    },
    tooltip:
      "Shows the count of new product installations for the top 10 competitors by installation count during the selected time period.\n\nThis helps track which competitors are most actively expanding their product footprint in the market.",

    defaultSelectedYear: "1",
  },
  competitorMarketPresence: {
    chartTitle: "Competitor Market Presence",
    baseId: "competitor-market-presence",
    csvFieldMappings: {
      segmentName: "Competitor Market",
      current: "Current",
      totalSpend: "Total Spend",
      totalCompanies: "Total Companies",
      totalPercentage: "Total Percentage",
      currentPercentage: "Current Percentage",
    },
    tooltip:
      "Shows competitor presence across your TAM, SAM, and SOM. Blue bars represent companies where competitors are detected, with percentages indicating penetration rates.\n\nUnderstand how saturated each market level is with competitive solutions to identify untapped opportunities.",
    legend: "COMPETITORS",
  },

  competitorPresenceByDepartment: {
    chartTitle: "Competitor Presence by Department",
    baseId: "competitor-presence-by-department",
    csvFieldMappings: {
      faiDepartmentName: "Department Names",
      installsCount: "# of Companies",
      marketPercentage: "Penetration %",
    },
    tooltip:
      "Maps competitor presence by functional department (all competitors). Each bar shows the number of companies with competitor installations in that department.\n\nUnderstand which business functions competitors target most frequently to focus your positioning strategy.",
  },
  competitorKeyDetection: {
    chartTitle: "Competitor Key Detection",
    baseId: "competitor-key-detection",
    tooltip:
      "Lists the top 5 highest-revenue companies where each competitor's product was detected in the last 6 months.\n\nIdentify strategic accounts where competitors have been recently detected representing competitive risks or opportunities.",
  },
  competitorNewProductDetectionsBySegment: {
    chartTitle: "Competitor New Product Detections by Segment",
    baseId: "competitor-new-product-detections-by-segment",
    tooltip:
      "Compares new product installations across different market segments. For each segment, shows the top 5 competitors by new installation count. Toggle between segment types and time periods.\n\nThis helps identify where competitors are focusing their expansion efforts.",
  },
  competitorNewProductDetectionsOverTime: {
    chartTitle: "Competitor New Product Detections Over Time",
    baseId: "competitor-new-product-detections-over-time",
    tooltip:
      "Tracks new installations over time for the top 5 competitors by new installation count. Each line shows how many new installations were detected during each time period.\n\nThis reveals which competitors have growing momentum and are actively expanding their product footprint.",
  },
  competitorMarketPenetrationTrend: {
    chartTitle: "Competitor Market Penetration Trend",
    baseId: "competitor-market-penetration-trend",
    tooltip:
      "Shows how market share has changed over time for the top 5 competitors based on their average market penetration over the selected period. Each line tracks a competitor's penetration percentage over the selected timeframe (1-5 years).\n\nMonitor competitive dynamics and identify which competitors are gaining or losing momentum",
    defaultSelectedPeriod: "1",
    topProducts: 5,
  },

  // Market Landscape
  keyPlayerMarketPenetration: {
    chartTitle: "Key Player Market Penetration",
    baseId: "key-player-market-penetration",
    csvFieldMappings: {
      segment: "Segment",
      companyCount: "# of Companies",
      percentage: "Penetration %",
      vendorName: "Vendor Name",
      categoryName: "Category Name",
    },
    tooltip:
      "Ranks top 10 products by installation count, showing the number of companies where each product is installed. Percentages indicate market penetration (installs divided by total companies in your active market).\n\nUnderstand the competitive landscape and identify dominant market players.",
  },
  keyPlayerNewProductDetections: {
    chartTitle: "Key Player New Product Detections",
    baseId: "key-player-new-product-detections",
    tooltip:
      "Tracks new installations detected over time for the top 5 key players. Each line shows how many new companies adopted each product during each time period.\n\nIdentify which products are gaining momentum and acquiring new customers most rapidly.",
  },
  keyPlayerProductDetectionsBySegment: {
    chartTitle: "Key Player Product Detections by Segment",
    baseId: "key-player-product-detections-by-segment",
    tooltip:
      "Compares new product installations across different market segments. For each segment, shows the top 5 products by new detection count. Toggle between segment types and time periods using the buttons above.\n\nSpot which segments are experiencing the most growth and competitive activity.",
  },
  keyPlayerMarketPenetrationBySegment: {
    chartTitle: "Key Player Market Penetration by Segment",
    baseId: "key-player-market-penetration-by-segment",
    tooltip:
      "Compares product penetration rates across different market segments (geography, industry, revenue range, employee range). For each segment, shows the top 5 products by penetration within that segment.\n\nNote: Products may have presence in other segments but only appear where they rank in the top 5 for that specific segment. Use toggle buttons above to switch between segment types. \n\nThis view helps you identify which products are dominant in specific market segments and where potential opportunities exist in less saturated segments.",
  },
  keyPlayerMarketPenetrationTrend: {
    chartTitle: "Key Player Market Penetration Trend",
    baseId: "key-player-market-penetration-trend",
    tooltip:
      "Shows how market share has changed over time for the top 5 key players in your selected product category. Each line tracks a product's penetration percentage over your selected timeframe (1-5 years). \n\nIdentify which players are gaining or losing market share to anticipate competitive shifts.",
  },

  marketSizeBySegment: {
    chartTitle: "Market Size by Segment",
    baseId: "market-size-by-segment-chart",
    csvFieldMappings: {
      segmentName: "Market size",
      current: "Current",
      whitespace: "Whitespace",
      totalSpend: "Total Spend",
      totalCompanies: "Total Companies",
      totalPercentage: "Total Percentage",
      currentPercentage: "Current Percentage",
      whitespacePercentage: "Potential Percentage",
      expansionPercentage: "Expansion Percentage",
      prospectPercentage: "Prospect Percentage",
    },
    tooltip:
      "Visualizes your TAM, SAM, and SOM with spend amounts and company counts. Blue bars show current customers, orange bars show potential customers. Percentages show each market's size relative to TAM.\n\nThis overview helps you understand your overall market opportunity and current market penetration at a glance.",
  },

  marketSizeByGeography: {
    chartTitle: "Market Size by Geography",
    baseId: "market-size-by-geography",
    csvFieldMappings: {
      category: "Geography",
      barSeriesValue: "Spend (USD)",
      lineSeriesValue: "No. Of Companies",
    },
    tooltip:
      "Ranks top geographic regions/countries by total spend (bars, left axis) and displays company counts (line, right axis). Toggle between regions and countries using the buttons above.\n\nIdentify the highest-value geographic markets to prioritize your sales and marketing efforts.",
  },
  marketSizeByIndustry: {
    chartTitle: "Market Size by Industry",
    baseId: "market-size-by-industry",
    csvFieldMappings: {
      category: "Industry",
      barSeriesValue: "Spend (USD)",
      lineSeriesValue: "No. Of Companies",
    },
    tooltip:
      "Ranks top industries by total spend (bars, left axis) and displays company counts (line, right axis). Toggle between HG Industries and NAICS classifications using the buttons above.\n\nTarget your go-to-market strategy toward the industries with the highest potential value.",
  },
  marketSizeByEmployee: {
    chartTitle: "Market Size by Employee",
    baseId: "market-size-by-employee",
    csvFieldMappings: {
      category: "Employee Range",
      barSeriesValue: "Spend (USD)",
      lineSeriesValue: "No. Of Companies",
    },
    tooltip:
      "Shows market distribution across company size segments based on employee count, with spend (bars, left axis) and company counts (line, right axis).\n\nDetermine which company size segments have the highest potential value to focus your resources effectively.",
  },
  marketSizeByRevenue: {
    chartTitle: "Market Size by Revenue",
    baseId: "market-size-by-revenue",
    csvFieldMappings: {
      category: "Revenue",
      barSeriesValue: "Spend (USD)",
      lineSeriesValue: "No. Of Companies",
    },
    tooltip:
      "Shows market distribution across company revenue segments, ranked by spend (bars, left axis) with company counts (line, right axis).\n\nIdentify which company revenue segments represent the largest market opportunity to inform your targeting strategy.",
  },
};
